import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { casesAPI, organizationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Upload,
  ScanLine,
  Loader2,
  CheckCircle,
  XCircle,
  X,
  FileImage,
  ArrowLeft,
  Plus,
  Trash2,
  Eye,
  Crown,
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const safeRender = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return `${v.length} item(s)`;
  return JSON.stringify(v);
};

const FIELD_LABELS = {
  name: 'Full Name', first_name: 'First Name', middle_name: 'Middle Name', last_name: 'Last Name',
  maiden_name: 'Maiden Name', title: 'Title', sex: 'Sex', marital_status: 'Marital Status',
  dob: 'Date of Birth', city_of_birth: 'City of Birth', state_of_birth: 'State of Birth',
  country_of_birth: 'Country of Birth', citizenship: 'Citizenship', ssn: 'SSN',
  a_number: 'A-Number', major_field_of_study: 'Field of Study', degree: 'Degree',
  email: 'Email', telephone: 'Phone', day_phone: 'Day Phone', cell_phone: 'Cell Phone',
  evening_phone: 'Evening Phone', address: 'Address', mailing_address: 'Mailing Address',
  country: 'Country', type_of_service: 'Service Type', date_of_arrival: 'Arrival Date',
  uscis_receipt: 'USCIS Receipt', nonimmigrant_status: 'Immigration Status',
  ethnicity: 'Ethnicity', race: 'Race', height: 'Height', weight: 'Weight',
  eye_color: 'Eye Color', hair_color: 'Hair Color',
  employer_name: 'Employer', employer_address: 'Employer Address', business_type: 'Business Type',
  job_title: 'Job Title', job_description: 'Job Description', supervisor_name: 'Supervisor',
  previous_addresses: 'Previous Addresses', previous_employment: 'Previous Employment',
  education: 'Education', marriages: 'Marriages', members: 'Children',
  abroad_trips: 'Trips Outside US', passports: 'Passports', other_names: 'Other Names Used',
  notes: 'Notes', form_type: 'Form Type',
  emergency_contact_name: 'Emergency Contact', emergency_contact_phone: 'Emergency Phone',
};

export default function BatchScanPage() {
  const { isSuperAdmin, organizationId, hasFeature } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState([]);
  const [creating, setCreating] = useState({});
  const [creatingAll, setCreatingAll] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(null);

  // Org selection for super admins
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [orgsLoaded, setOrgsLoaded] = useState(false);

  React.useEffect(() => {
    if (isSuperAdmin && !orgsLoaded) {
      organizationsAPI.getAll().then(res => {
        setOrganizations(res.data.filter(o => o.is_active));
        setOrgsLoaded(true);
      }).catch(() => toast.error('Failed to load organizations'));
    }
  }, [isSuperAdmin, orgsLoaded]);

  const handleFilesSelected = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter(f => {
      const ok = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(f.type);
      if (!ok) toast.error(`${f.name}: unsupported file type`);
      return ok && f.size <= 10 * 1024 * 1024;
    });
    if (valid.length + files.length > 20) {
      toast.error('Maximum 20 files per batch');
      return;
    }
    setFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleScan = async () => {
    if (files.length === 0) return toast.error('Add files first');
    setScanning(true);
    setResults([]);
    
    // Process files one at a time to avoid production proxy timeouts
    const allResults = [];
    for (let i = 0; i < files.length; i++) {
      toast.info(`Scanning file ${i + 1} of ${files.length}: ${files[i].name}`);
      try {
        const res = await casesAPI.scanForm(files[i]);
        allResults.push({
          filename: files[i].name,
          success: true,
          extracted_data: res.data.extracted_data,
          form_type: res.data.form_type,
        });
      } catch (err) {
        allResults.push({
          filename: files[i].name,
          success: false,
          error: err.response?.data?.detail || 'Scan failed',
        });
      }
      setResults([...allResults]);
    }
    
    const succeeded = allResults.filter(r => r.success).length;
    toast.success(`Scanned ${succeeded}/${files.length} forms successfully`);
    setScanning(false);
  };

  const mapToCase = (data) => {
    const parts = (data.name || '').trim().split(/\s+/);
    const firstName = data.first_name || parts[0] || '';
    const lastName = data.last_name || (parts.length > 1 ? parts[parts.length - 1] : '');
    const middleName = data.middle_name || (parts.length > 2 ? parts.slice(1, -1).join(' ') : '');
    
    const result = {
      name: data.name || [firstName, middleName, lastName].filter(Boolean).join(' '),
      first_name: firstName, middle_name: middleName, last_name: lastName,
    };
    // Map ALL scalar fields from extracted data to case fields
    const scalarFields = [
      'maiden_name', 'title', 'sex', 'marital_status', 'dob',
      'city_of_birth', 'state_of_birth', 'country_of_birth', 'citizenship',
      'ssn', 'a_number', 'major_field_of_study', 'degree',
      'email', 'telephone', 'day_phone', 'cell_phone', 'evening_phone', 'fax',
      'address', 'mailing_address', 'state', 'county', 'country',
      'date_of_arrival', 'uscis_receipt', 'nonimmigrant_status',
      'nonimmigrant_status_exp_date', 'biometrics_date',
      'ethnicity', 'race', 'height', 'weight', 'eye_color', 'hair_color',
      'employer_name', 'employer_address', 'business_type', 'job_title',
      'job_description', 'supervisor_name', 'hours_per_week', 'employer_email',
      'emergency_contact_name', 'emergency_contact_phone',
    ];
    for (const f of scalarFields) {
      if (data[f]) result[f] = data[f];
    }
    // Remap type_of_service -> type_of_services
    if (data.type_of_service) result.type_of_services = data.type_of_service;
    if (!result.country && data.country_of_birth) result.country = data.country_of_birth;
    
    // Map ALL list fields
    const listFields = [
      'other_names', 'previous_addresses', 'previous_employment', 'education',
      'marriages', 'members', 'abroad_trips', 'passports', 'visas', 'i94s',
    ];
    for (const f of listFields) {
      if (Array.isArray(data[f]) && data[f].length > 0) result[f] = data[f];
    }
    return result;
  };

  const createCase = async (idx) => {
    const r = results[idx];
    if (!r?.success) return;
    if (isSuperAdmin && !selectedOrgId) return toast.error('Select an organization first');
    setCreating(prev => ({ ...prev, [idx]: true }));
    try {
      const orgId = isSuperAdmin ? selectedOrgId : organizationId;
      await casesAPI.create(mapToCase(r.extracted_data), orgId);
      setResults(prev => prev.map((item, i) => i === idx ? { ...item, created: true } : item));
      toast.success(`Case "${r.extracted_data.name || r.filename}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create case');
    } finally {
      setCreating(prev => ({ ...prev, [idx]: false }));
    }
  };

  const createAllCases = async () => {
    const pending = results.filter((r, i) => r.success && !r.created);
    if (pending.length === 0) return toast.error('No cases to create');
    if (isSuperAdmin && !selectedOrgId) return toast.error('Select an organization first');
    setCreatingAll(true);
    const orgId = isSuperAdmin ? selectedOrgId : organizationId;
    let created = 0;
    for (let i = 0; i < results.length; i++) {
      if (!results[i].success || results[i].created) continue;
      try {
        await casesAPI.create(mapToCase(results[i].extracted_data), orgId);
        setResults(prev => prev.map((item, idx) => idx === i ? { ...item, created: true } : item));
        created++;
      } catch {
        // continue with next
      }
    }
    toast.success(`Created ${created} cases`);
    setCreatingAll(false);
  };

  const handleReset = () => {
    setFiles([]);
    setResults([]);
    setCreating({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const previewResult = previewIdx !== null ? results[previewIdx] : null;
  const successCount = results.filter(r => r.success).length;
  const createdCount = results.filter(r => r.created).length;
  const pendingCreate = results.filter(r => r.success && !r.created).length;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/cases')}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground font-body text-sm mb-4 transition-colors"
            data-testid="back-to-cases"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Cases
          </Button>
          <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">
            Batch Form Scanner
          </h1>
          <p className="font-body text-muted-foreground">
            Upload multiple immigration forms and create cases from AI-extracted data
          </p>
        </div>

        {/* Locked Premium Gate */}
        {!hasFeature('batch_scanner') ? (
          <Card className="border-dashed max-w-lg" data-testid="batch-scanner-locked">
            <CardContent className="py-12 text-center">
              <Crown className="w-10 h-10 text-amber-500 mx-auto mb-4" />
              <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20 hover:bg-amber-500/15 mb-3">Premium Feature</Badge>
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">Batch Form Scanner</h3>
              <p className="font-body text-sm text-muted-foreground max-w-sm mx-auto">
                Batch scanning is a premium add-on that lets you upload multiple immigration forms and create cases automatically using AI. Contact your administrator to enable this feature.
              </p>
            </CardContent>
          </Card>
        ) : (
        <>

        {/* Org selector for super admin */}
        {isSuperAdmin && (
          <Card className="mb-6" data-testid="org-selector-card">
            <CardContent className="pt-6">
              <Label className="font-body mb-2 block">Organization *</Label>
              <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                <SelectTrigger className="max-w-md font-body" data-testid="batch-org-select">
                  <SelectValue placeholder="Select an organization" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* Upload Area */}
        {results.length === 0 && (
          <Card className="mb-6 border-dashed" data-testid="batch-upload-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
                <ScanLine className="w-5 h-5 text-primary" />
                Upload Forms
              </CardTitle>
              <CardDescription className="font-body">
                Select up to 20 scanned immigration forms (JPEG, PNG, WEBP, PDF — max 10MB each)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                multiple
                onChange={handleFilesSelected}
                className="hidden"
                data-testid="batch-file-input"
              />

              {files.length === 0 ? (
                <div className="text-center py-10">
                  <FileImage className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="batch-select-files-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Select Files
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* File list */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg border border-border bg-card" data-testid={`batch-file-${i}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <FileImage className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="font-body text-sm truncate">{f.name}</span>
                          <span className="font-body text-xs text-muted-foreground flex-shrink-0">
                            {(f.size / 1024).toFixed(0)} KB
                          </span>
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeFile(i)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={files.length >= 20}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add More
                    </Button>
                    <Button
                      onClick={handleScan}
                      disabled={scanning}
                      className="bg-primary text-white"
                      data-testid="batch-scan-btn"
                    >
                      {scanning ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Scanning {files.length} files...
                        </>
                      ) : (
                        <>
                          <ScanLine className="w-4 h-4 mr-2" />
                          Scan All ({files.length})
                        </>
                      )}
                    </Button>
                    <Button variant="ghost" onClick={handleReset} className="text-muted-foreground">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Scanning Progress */}
        {scanning && (
          <Card className="mb-6">
            <CardContent className="py-12 text-center">
              <Loader2 className="w-10 h-10 text-primary mx-auto mb-4 animate-spin" />
              <p className="font-heading font-semibold text-foreground">
                AI is scanning {files.length} forms...
              </p>
              <p className="font-body text-sm text-muted-foreground mt-1">
                This may take a minute. Processing up to 5 forms in parallel.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {results.length > 0 && !scanning && (
          <>
            {/* Summary Bar */}
            <Card className="mb-6" data-testid="batch-results-summary">
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-body font-medium">{successCount} extracted</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-red-500" />
                      <span className="font-body font-medium">{results.length - successCount} failed</span>
                    </div>
                    {createdCount > 0 && (
                      <Badge variant="secondary" className="font-body">{createdCount} created</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {pendingCreate > 0 && (
                      <Button
                        onClick={createAllCases}
                        disabled={creatingAll}
                        className="bg-primary text-white"
                        data-testid="create-all-cases-btn"
                      >
                        {creatingAll ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-2" />
                        )}
                        Create All Cases ({pendingCreate})
                      </Button>
                    )}
                    <Button variant="outline" onClick={handleReset} data-testid="batch-reset-btn">
                      Scan New Batch
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Result Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {results.map((r, idx) => (
                <Card
                  key={idx}
                  className={`transition-all ${r.created ? 'border-green-300 dark:border-green-700 bg-green-50/30 dark:bg-green-900/20' : r.success ? 'border-border' : 'border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-900/20'}`}
                  data-testid={`batch-result-${idx}`}
                >
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-body text-sm font-medium text-foreground truncate">
                          {r.filename}
                        </p>
                        {r.success && r.form_type && r.form_type !== 'Unknown' && (
                          <Badge variant="secondary" className="text-xs mt-1">{r.form_type}</Badge>
                        )}
                      </div>
                      {r.created ? (
                        <Badge className="bg-green-600 text-white text-xs flex-shrink-0">Created</Badge>
                      ) : r.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      )}
                    </div>

                    {r.success ? (
                      <>
                        <div className="bg-muted/50 rounded-lg p-2.5 mb-3 space-y-1 max-h-28 overflow-y-auto">
                          {Object.entries(r.extracted_data)
                            .filter(([k, v]) => v && k !== 'form_type' && typeof v === 'string' && v !== '')
                            .slice(0, 4)
                            .map(([k, v]) => (
                              <div key={k} className="flex gap-2 text-xs">
                                <span className="font-body font-medium text-muted-foreground min-w-[80px]">
                                  {FIELD_LABELS[k] || k.replace(/_/g, ' ')}:
                                </span>
                                <span className="font-body text-foreground truncate">{safeRender(v)}</span>
                              </div>
                            ))}
                          {(() => {
                            const scalarCount = Object.entries(r.extracted_data).filter(([k, v]) => v && k !== 'form_type' && typeof v === 'string' && v !== '').length;
                            const listCount = Object.entries(r.extracted_data).filter(([k, v]) => Array.isArray(v) && v.length > 0).length;
                            return (
                              <>
                                {scalarCount > 4 && <p className="text-xs text-muted-foreground">+{scalarCount - 4} more fields</p>}
                                {listCount > 0 && <p className="text-xs text-primary font-medium">{listCount} list section(s) detected</p>}
                              </>
                            );
                          })()}
                        </div>
                        <div className="flex gap-2">
                          {!r.created && (
                            <Button
                              size="sm"
                              onClick={() => createCase(idx)}
                              disabled={creating[idx]}
                              className="flex-1 bg-primary text-white"
                              data-testid={`create-case-${idx}`}
                            >
                              {creating[idx] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                              Create Case
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => setPreviewIdx(idx)} data-testid={`preview-${idx}`}>
                            <Eye className="w-3 h-3 mr-1" />
                            Details
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="font-body text-sm text-red-600">{safeRender(r.error)}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
        </>
        )}
      </main>

      {/* Preview Dialog */}
      <Dialog open={previewIdx !== null} onOpenChange={() => setPreviewIdx(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {previewResult?.filename}
            </DialogTitle>
            <DialogDescription className="font-body">
              {previewResult?.form_type && previewResult.form_type !== 'Unknown'
                ? `Detected form: ${previewResult.form_type}`
                : 'Extracted data'}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2 py-2">
            {previewResult?.extracted_data &&
              Object.entries(previewResult.extracted_data)
                .filter(([k, v]) => v && k !== 'form_type' && (typeof v === 'string' ? v !== '' : (Array.isArray(v) ? v.length > 0 : true)))
                .map(([k, v]) => (
                  <div key={k} className="flex items-start gap-3 text-sm border-b border-border pb-2">
                    <span className="font-body font-medium text-muted-foreground min-w-[130px]">
                      {FIELD_LABELS[k] || k.replace(/_/g, ' ')}
                    </span>
                    <span className="font-body text-foreground">
                      {safeRender(v)}
                    </span>
                  </div>
                ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewIdx(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
